import {ScaleLinear} from 'd3';
import * as d3 from 'd3';
import React from 'react';

import BenchlingConfig from 'BenchlingConfig';
import {Sequence} from 'components/Sequence';
import {PolymerLayout, SliceMapType} from 'constants/MapsConstants';
import FileStates from 'FileStates';
import {BioentityPopoverContent} from 'molbio/BioentityPopoverContent';
import ComponentWrapper from 'molbio/maps/linear/ComponentWrapper';
import {useRedraw} from 'molbio/maps/linear/useRedraw';
import {
  PART_ANNOTATION_COLOR,
  PART_ANNOTATION_OUT_OF_SYNC_COLOR,
  PART_NAME_FONT_SIZE,
  PART_STROKE_COLOR,
} from 'molbio/maps/PartsViewConstants';
import splitPartsIntoHighlightRegions, {
  PartHighlightRegion,
} from 'molbio/maps/splitPartsIntoHighlightRegions';
import Type from 'molbio/maps/Type';
import * as sequtil from 'molbio/SeqUtil';
import {TooltipWrapper} from 'ui-library/bs/Tooltip';
import Popover from 'ui-library/floating-ui/Popover';

const TOP_LINE_HEIGHT = 5;
const FILL_OPACITY = 0.75;
const STROKE_WIDTH = 1;
const STROKE_OPACITY = 1;

function getFillColor(region: PartHighlightRegion): string {
  const baseColor = d3.rgb(region.baseFillColor);
  return region.isOverlap ? baseColor.darker().toString() : baseColor.toString();
}

function getFillOpacity(region: PartHighlightRegion): number {
  let fillOpacity = FILL_OPACITY;
  // For non-overlapping parts, make the shading lighter to indicate archived. For
  // overlapping parts, the part annotation is lighter.
  if (!BenchlingConfig.PARTS_ENABLE_OVERLAPPING_PARTS && region.isArchived) {
    fillOpacity *= 0.5;
  }
  return fillOpacity;
}

function getStrokeColor(region: PartHighlightRegion): string {
  const baseColor = d3.rgb(PART_STROKE_COLOR);
  return region.isOverlap ? baseColor.darker().toString() : baseColor.toString();
}

function getTopLineColor(region: PartHighlightRegion): string {
  const baseColor = region.outOfSync
    ? d3.rgb(PART_ANNOTATION_OUT_OF_SYNC_COLOR)
    : d3.rgb(PART_ANNOTATION_COLOR);
  return region.isOverlap ? baseColor.darker().toString() : baseColor.toString();
}

function getPartRegionsForSlice(start: number, end: number, sequence: Sequence): Array<PartHighlightRegion> {
  const sequenceLength = sequence.length();
  // Sometimes, we have extra slices past the end of the sequence. This causes warnings when getting the
  // relevant parts, so we early return in those cases, and clamp the end such that start/end is always in
  // the [0, seqLength] range.
  if (start > sequenceLength) {
    return [];
  }
  const clampedEnd = Math.min(end, sequenceLength);
  const parts = sequence.parts().getSequencePartsBetween(start, clampedEnd);
  return splitPartsIntoHighlightRegions(parts, sequence);
}

function getRectangularPath(
  partRegion: PartHighlightRegion,
  sliceStart: number,
  sliceEnd: number,
  xFn: ScaleLinear<number, number>,
  y: number,
  height: number,
  sequence: Sequence
): string {
  const start = Math.max(sliceStart, partRegion.start);
  const end = Math.min(
    sliceEnd,
    partRegion.start + sequtil.length(partRegion.start, partRegion.end, sequence.length())
  );

  const xStart = xFn(start);
  const xEnd = xFn(end);
  return `M ${xStart} ${y} L ${xEnd} ${y} L ${xEnd} ${y + height} L ${xStart} ${y + height} Z`;
}

function getPartLabelText(partRegion: PartHighlightRegion): string {
  if (partRegion.isArchived) {
    return `${partRegion.name} (archived)`;
  } else {
    return partRegion.name;
  }
}

function getHeight(dnaHeight: number, dnaSpacer: number, dnaComplement: boolean): number {
  if (dnaComplement) {
    return 2 * (dnaHeight + dnaSpacer);
  } else {
    return dnaHeight + dnaSpacer;
  }
}

function getNameRelativeX(
  partRegion: PartHighlightRegion,
  type: Type,
  sequenceLength: number,
  xFn: ScaleLinear<number, number>,
  labelText: string
): number {
  const start = partRegion.start;
  const end = start + sequtil.length(start, partRegion.end, sequenceLength);
  const startX = xFn(start);
  const endX = xFn(end);
  return (endX + startX - type.width(labelText.length)) / 2;
}

interface PartsViewProps {
  /** A function that takes the index of a residue and returns its x-axis position on the slice */
  xFn: ScaleLinear<number, number>;
  /** The index at which the slice starts */
  sliceStart: number;
  /** The index at which the slice ends */
  sliceEnd: number;
  /** The Backbone sequence being rendered */
  sequence: Sequence;
  /** Flat object layout data */
  polymerLayout: PolymerLayout;
  /** Height in pixels of the DNA residues at default zoom. */
  dnaHeight: number;
  /** Height in pixels of the space below the DNA residues at default zoom. */
  dnaSpacer: number;
  /** Whether or not the complement slice is visible */
  complementVisible: boolean;
  /** What kind of map we're drawing on */
  mapType: SliceMapType;
  /**
   * A function that accepts a new box height or a way to calculate it and triggers a redraw with the new box
   * height.
   * */
  redraw: (newBoxHeight?: number | ((layout: PolymerLayout) => number)) => void;
}

export default function PartsView({
  xFn,
  sliceStart,
  sliceEnd,
  sequence,
  polymerLayout,
  dnaHeight,
  dnaSpacer,
  complementVisible,
  mapType,
  redraw,
}: PartsViewProps): JSX.Element {
  const partRegions = getPartRegionsForSlice(sliceStart, sliceEnd, sequence);

  const type = new Type(PART_NAME_FONT_SIZE);
  const boxHeight = type.height() + 2;

  useRedraw({
    redraw,
    shouldRedraw: () => partRegions.length > 0,
    newBoxHeight: boxHeight,
  });

  const topLineY = boxHeight - TOP_LINE_HEIGHT;

  return (
    <ComponentWrapper layout={polymerLayout} shouldStopMouseDownPropagation={false}>
      <>
        {partRegions.map((partRegion: PartHighlightRegion, index: number) => {
          const labelText = getPartLabelText(partRegion);
          const startX = xFn(partRegion.start);
          const nameRelativeX = getNameRelativeX(partRegion, type, sequence.length(), xFn, labelText);

          return (
            <g key={`${partRegion.name}-${index}`}>
              <path
                d={getRectangularPath(
                  partRegion,
                  sliceStart,
                  sliceEnd,
                  xFn,
                  boxHeight,
                  getHeight(dnaHeight, dnaSpacer, complementVisible),
                  sequence
                )}
                stroke={getStrokeColor(partRegion)}
                strokeWidth={STROKE_WIDTH}
                strokeOpacity={STROKE_OPACITY}
                fill={getFillColor(partRegion)}
                fillOpacity={getFillOpacity(partRegion)}
              />
              {BenchlingConfig.PARTS_ENABLE_OVERLAPPING_PARTS && (
                // For overlapping parts, the part name is displayed in annotations just below the
                // DNA, instead of above it. See SequencePartAnnotationsView.ts.
                <path
                  d={getRectangularPath(
                    partRegion,
                    sliceStart,
                    sliceEnd,
                    xFn,
                    topLineY,
                    TOP_LINE_HEIGHT,
                    sequence
                  )}
                  fill={getTopLineColor(partRegion)}
                  fillOpacity={FILL_OPACITY}
                />
              )}
              {!BenchlingConfig.PARTS_ENABLE_OVERLAPPING_PARTS && (
                <TooltipWrapper tooltipTitle={mapType === SliceMapType.minimap ? labelText : null}>
                  <Popover
                    renderContent={() => (
                      <BioentityPopoverContent
                        // When there are no overlapping part regions, the partRef will always be defined.
                        id={partRegion.partRef!.sequence().id}
                        onClickName={() => {
                          FileStates.get().openFolderItem(partRegion.partRef!.sequence().id);
                        }}
                        validationExtras={null}
                      />
                    )}
                    isDisabled={mapType === SliceMapType.minimap}
                  >
                    <text
                      fontSize={type.fontSize}
                      dx={nameRelativeX < startX ? startX : nameRelativeX}
                      dy={type.height()}
                    >
                      {nameRelativeX < startX ? '' : labelText}
                    </text>
                  </Popover>
                </TooltipWrapper>
              )}
            </g>
          );
        })}
      </>
    </ComponentWrapper>
  );
}
